import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { useI18n } from '@/lib/i18n'
import { useTRPC } from '@/lib/trpc'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Eye, EyeOff, Key, Loader2, Save, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { toast } from 'sonner'

export interface ByomProviderConfig {
  apiKey: string
  modelId: string
  endpoint: string
}

export function getByomProviderStorageKey(providerId: string, field: 'apiKey' | 'modelId' | 'endpoint') {
  return `mentiss_byom_${providerId}_${field}`
}

export function loadByomProviderConfig(_providerId: string): ByomProviderConfig | null {
  return null
}

export function ByomTab() {
  const { t } = useI18n()
  const trpc = useTRPC()
  const queryClient = useQueryClient()

  const { data: dbCustom, isLoading } = useQuery(
    trpc.userRouter.getCustomEndpoint.queryOptions(),
  )

  const { mutateAsync: updateCustomEndpoint, isPending: isSaving } = useMutation(
    trpc.userRouter.updateCustomEndpoint.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.userRouter.getCustomEndpoint.queryKey() })
      },
    }),
  )

  const [endpoint, setEndpoint] = useState('')
  const [apiKey, setApiKey] = useState('')
  const [modelId, setModelId] = useState('')
  const [showApiKey, setShowApiKey] = useState(false)

  const [savedSnapshot, setSavedSnapshot] = useState({ endpoint: '', apiKey: '', modelId: '' })

  const [dbLoaded, setDbLoaded] = useState(false)
  useEffect(() => {
    if (!dbCustom || dbLoaded)
      return
    const { customEndpoint, customApiKey, customModelId } = dbCustom
    setEndpoint(customEndpoint || '')
    setApiKey(customApiKey || '')
    setModelId(customModelId || '')
    setSavedSnapshot({
      endpoint: customEndpoint || '',
      apiKey: customApiKey || '',
      modelId: customModelId || '',
    })
    setDbLoaded(true)
  }, [dbCustom, dbLoaded])

  const isConfigured = !!apiKey && !!endpoint
  const hasChanges = endpoint !== savedSnapshot.endpoint
    || apiKey !== savedSnapshot.apiKey
    || modelId !== savedSnapshot.modelId

  const handleSave = useCallback(async () => {
    await updateCustomEndpoint({
      customEndpoint: endpoint,
      customApiKey: apiKey,
      customModelId: modelId,
    })
    setSavedSnapshot({ endpoint, apiKey, modelId })
    toast.success(t('auth.dashboard.byomSaved'))
  }, [endpoint, apiKey, modelId, t, updateCustomEndpoint])

  const handleClear = useCallback(async () => {
    setEndpoint('')
    setApiKey('')
    setModelId('')
    setSavedSnapshot({ endpoint: '', apiKey: '', modelId: '' })
    await updateCustomEndpoint({
      customEndpoint: '',
      customApiKey: '',
      customModelId: '',
    })
    toast.success(t('auth.dashboard.byomCleared'))
  }, [t, updateCustomEndpoint])

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Key className="size-5" />
          {t('auth.dashboard.byomTitle')}
        </CardTitle>
        <CardDescription>
          {t('auth.dashboard.byomDescription')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="size-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            { }
            <div className="flex items-center gap-2 rounded-lg border p-3">
              <div className={`size-2.5 rounded-full ${isConfigured ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'}`} />
              <span className="text-sm text-muted-foreground">
                {isConfigured
                  ? t('auth.dashboard.byomStatusConfigured')
                  : t('auth.dashboard.byomStatusNotConfigured')}
              </span>
              {isConfigured && (
                <button
                  type="button"
                  onClick={handleClear}
                  className="ml-auto rounded p-1 text-muted-foreground hover:text-destructive"
                  title={t('auth.dashboard.byomClearButton')}
                >
                  <Trash2 className="size-3.5" />
                </button>
              )}
            </div>

            { }
            <div className="rounded-lg border border-sky-200 bg-sky-50/50 p-4 dark:border-sky-800/30 dark:bg-sky-950/20">
              <p className="text-sm text-sky-700 dark:text-sky-300">
                {t('auth.dashboard.byomInfo')}
              </p>
            </div>

            { }
            <div className="space-y-4 rounded-lg border border-primary/30 bg-primary/5 p-4">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold">Custom Endpoint</h3>
                <span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-medium text-primary">Your Model</span>
              </div>

              { }
              <div className="space-y-1">
                <Label htmlFor="byom-endpoint" className="text-xs">
                  {t('game.byomEndpointLabel')}
                </Label>
                <input
                  id="byom-endpoint"
                  type="url"
                  value={endpoint}
                  onChange={e => setEndpoint(e.target.value)}
                  placeholder="https://your-endpoint.com/v1"
                  className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-xs placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>

              { }
              <div className="space-y-1">
                <Label htmlFor="byom-apikey" className="text-xs">
                  {t('game.byomApiKeyLabel')}
                </Label>
                <div className="relative">
                  <input
                    id="byom-apikey"
                    type={showApiKey ? 'text' : 'password'}
                    value={apiKey}
                    onChange={e => setApiKey(e.target.value)}
                    placeholder="sk-..."
                    className="w-full rounded-md border border-border bg-background px-3 py-1.5 pr-9 text-xs placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                  />
                  <button
                    type="button"
                    onClick={() => setShowApiKey(!showApiKey)}
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-muted-foreground hover:text-foreground"
                  >
                    {showApiKey ? <EyeOff className="size-3.5" /> : <Eye className="size-3.5" />}
                  </button>
                </div>
              </div>

              { }
              <div className="space-y-1">
                <Label htmlFor="byom-modelid" className="text-xs">
                  {t('game.byomModelIdLabel')}
                </Label>
                <input
                  id="byom-modelid"
                  type="text"
                  value={modelId}
                  onChange={e => setModelId(e.target.value)}
                  placeholder="my-model-name"
                  className="w-full rounded-md border border-border bg-background px-3 py-1.5 text-xs placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
            </div>

            { }
            <div className="flex items-center justify-end gap-4 border-t pt-4">
              <Button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className="shrink-0 gap-2"
              >
                {isSaving ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : (
                  <Save className="size-4" />
                )}
                {t('auth.dashboard.byomSaveButton')}
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}
